import { Anchor } from '@components/atoms/Anchor';
import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import Hr from '@components/atoms/Hr';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import {
  Heading1,
  Heading2,
  Heading3,
  Paragraph,
} from '@components/atoms/Typography';
import GridLinkList from '@components/molecules/list/GridLinkList';
import PageContainer from '@components/molecules/PageContainer';
import CompareStatsForm from '@components/organisms/forms/CompareStatsForm';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import motorcycleStats from '@lib/helpers/motorcycleStats';
import { renameKeys } from 'helpers';
import { GetServerSideProps } from 'next';
import { BreadcrumbJsonLd } from 'next-seo';
import { useAmp } from 'next/amp';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

const FlexSection = styled(Section)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const TableSection = styled(Section)`
  padding-top: 16px;
  padding-bottom: 16px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 16px 0;
  }
`;

const Table = styled.table`
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;

  tr:nth-of-type(odd) {
    background: ${COLORS.lightGray};
  }

  tbody td,
  tbody th {
    padding: 5px;
  }

  th {
    background: ${COLORS.black};
    color: #fff;
    text-align: left;
  }

  td {
    padding: 5px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    tr td:first-child {
      font-weight: bold;
    }

    th,
    td {
      display: block;
    }

    td:before {
      content: attr(data-key);
      display: block;
      font-size: 12px;
      color: #999;
      text-transform: uppercase;
    }
  }
`;

const Aside = styled.aside`
  flex: 0 0 285px;
  max-width: 285px;
  margin-left: 70px;

  @media screen and (max-width: ${BREAKPOINTS.desk}) {
    display: none;
  }
`;

const InnerAside = styled.div`
  transform: translateZ(0);
  padding-right: 10px;
  padding-top: 50px;
  position: sticky;
  top: 100px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    position: initial;
    padding: 0;
  }
`;

const Wrapper = styled.div`
  margin-right: auto;
  margin-left: auto;
  padding-right: 15px;
  padding-left: 15px;
  display: flex;

  background-color: #fff;
  padding: 32px;
  border-radius: 6px;

  @media screen and (max-width: ${BREAKPOINTS.desk}) {
    flex-wrap: wrap;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 0;
  }
`;

interface Props {
  stats: Record<string, any>;
  allStats: any[];
}

const Header = styled(Section)`
  padding-top: 120px;
  padding-bottom: 32px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 92px;
  }
`;

const ModelStats = ({ stats, allStats }: Props) => {
  const isAmp = useAmp();

  const keysToRename = ['Combustível', 'Eletrônica', 'Transmissão'];

  const {
    brandName,
    model,
    modelName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    brand,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    id,
    Motor,
    Combustivel,
    Eletronica,
    Transmissao,
    ...rest
  } = renameKeys(stats, {
    [keysToRename[0]]: 'Combustivel',
    [keysToRename[1]]: 'Eletronica',
    [keysToRename[2]]: 'Transmissao',
  });

  const keys = Object.keys(rest);

  const statsFromBrand = allStats.filter((s) => s.brand === brand);

  const dc = (Motor['Diâmetro x Curso'] as string | null)?.split('x'); // Diâmetro x Curso
  dc?.map((x) => parseFloat(x.replace(',', '.')));

  let motorType = '';

  if (dc && dc[0] > dc[1]) {
    motorType = 'superquadrado';
  } else if (dc && dc[0] < dc[1]) {
    motorType = 'subquadrado';
  } else {
    motorType = 'quadrado';
  }

  let pros = '';
  let contras = '';

  if (Transmissao['Transmissão final'] === 'Por corrente') {
    pros =
      'baixo custo de aquisição, facilidade em encontrar peças, reposição simples.';
    contras = 'durabilidade baixa, acúmulo de sujeira, barulho ao rodar.';
  } else if (Transmissao['Transmissão final'] === 'Por correia dentada') {
    pros = 'durabilidade elevada, leveza dos componentes.';
    contras =
      'dificuldade em encontrar peças de reposição, troca um pouco complicada.';
  } else {
    pros = 'livre de manutenção regular, durabilidade, proteção.';
    contras = 'custo de peças elevado, manutenção exige bastante conhecimento.';
  }

  const category = stats['Outras informações']
    ? (stats['Outras informações'].Categoria as string)
    : null;

  let caracts = '';

  if (category === 'Street') {
    caracts =
      'a posição de pilotagem sentada, motores de 125 a 500 cilindradas, lugar para garupa, poucos acessórios e velocidades máximas entre 110 e 160 km/h, sendo muito utilizadas no Brasil pois são acessíveis e ideias para circulaçào na cidade.';
  } else if (
    category === 'Scooter' ||
    category === 'Utilitária' ||
    category === 'Cub'
  ) {
    caracts =
      'o cãmbio automático, escudo de proteção contra vento e cilindradas baixas (normalmente entre 50 e 150), elas são perfeitas para o aslfato. Rápidas, práticas e econômicas, se encaixam bem no dia a dia corrido das grandes cidades.';
  } else if (category === 'Naked') {
    caracts =
      'a ausência de carenagens, que servem como proteções na moto. Conseguem prover uma boa mobilidade possuindo motores de cilindrada média. A ausência de carenagem deixa a moto mais veloz mas trás como consequência a falta de proteção contra o vento.';
  } else if (
    category === 'Sport' ||
    category === 'Super sport' ||
    category === 'Hiper sport'
  ) {
    caracts =
      'principais um design aerodinâmico estiloso e motor de alto desempenho, perfeitas para o uso em competições de moto. Com foco em desempenho ela deixa a desejar no conforto, com suspensões rígidas e bancos pouco aconchegantes.';
  } else if (
    category === 'On / Off Road' ||
    category === 'Dual-purpose' ||
    category === 'Trail' ||
    category === 'Super Motard'
  ) {
    caracts =
      'principais a flexibilidade de serem usadas tanto no dia a dia na cidade quanto em trilhas e viagens, e a diversidade de configuraçòes dos modelos, que podem previlegiar características de velocidade ou conforto, o seguro desse tipo de modelo também é bem variado conforme a utilizaçào.';
  } else if (category === 'Café Racer' || category === 'Custom') {
    caracts =
      'principais o design voltado para o conforto e possui o padrão on road, ou seja, feitas para pegar a estrada. Com banco baixo, apoio para o garupa e manopla elevada podemos notar que conforto não falta para longas horas (ou dias) de viagem.';
  }

  return (
    <PageContainer
      title={`Ficha técnica da ${brandName} ${modelName}`}
      metaDescription={`Ficha técnica com as especificações completas da ${brandName} ${model}. Veja detalhes de motor, performance, valores, seguro e muito mais nesse guia!`}
    >
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Ficha técnica',
            item: `${env.baseUrl}${ROUTES.stats}`,
          },
          {
            position: 3,
            name: `Ficha técnica para motos da ${brandName}`,
            item: `${env.baseUrl}${ROUTES.brandStats(brand)}`,
          },
          {
            position: 4,
            name: `Ficha técnica da ${brandName} ${modelName}`,
            item: `${env.baseUrl}${ROUTES.modelStats(brand, model)}`,
          },
        ]}
      />
      <Wrapper>
        <div>
          <Header>
            <Heading1>
              Ficha técnica da
              {` ${brandName} ${modelName}`}
            </Heading1>
          </Header>
          <Section style={{ paddingTop: 16, paddingBottom: 16 }}>
            <Heading2>Motor</Heading2>
            <Paragraph>
              O modelo
              <strong>{` ${modelName} `}</strong>
              da marca{' '}
              <strong>
                <Link href={ROUTES.brandStats(brand)} passHref>
                  <Anchor>{brandName}</Anchor>
                </Link>
              </strong>{' '}
              possui um motor com refrigeração
              <strong>{` ${Motor['Refrigeração']}`}</strong>, que é considerado
              o
              <strong>
                {Motor['Refrigeração'] === 'líquida'
                  ? ' mais eficiente '
                  : ' menos eficiente '}
              </strong>
              devido à maior complexidade e potência dos sistemas dos motores
              que existem atualmente.
              <br />
              <br />A capacidade cúbica (cilindrada) é o espaço disponível nos
              cilindros e é uma das características mais importantes para
              mostrar a potência do motor, nesse modelo temos uma capacidade
              cúbica de
              <strong>{` ${Motor['Capacidade cúbica']}`}</strong>
              ).
              <br />
              <br />A relação diâmetro x curso nesse modelo é
              <strong>{` ${Motor['Diâmetro x Curso']} `}</strong>e tem muito
              impacto no projeto de cada motor, pois vários componentes dependem
              dessa medida, o motor desse modelo é chamado de
              <strong>{` ${motorType} `}</strong>
              devido à relação diâmetro vs curso.
              <br />
              <br />O torque do motor podemos interpretar como sua agilidade
              inicial, tem relação com a arrancada que o motor consegue
              desempenhar no início da sua movimentação, já a potência é
              relativa a velocidade que o motor desempenha já estando em
              movimento. A potência máxima desse modelo é
              <strong>{` ${Motor['Potência máxima']} `}</strong>e possui um
              torque máximo de
              <strong>{` ${Motor['Torque máximo']}`}</strong>
              <br />
              <br />
              Confira abaixo os detalhes do motor:
            </Paragraph>
          </Section>
          <TableSection>
            <Table>
              <tbody>
                <tr>
                  <th colSpan={2}>
                    <strong>Motor</strong>
                  </th>
                </tr>
                {Object.entries(Motor).map((entry: any) => (
                  <tr key={entry[0]}>
                    <td>{entry[0]}</td>
                    <td>{entry[1].replace('- Mais informações', '')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableSection>

          <Section style={{ paddingTop: 16, paddingBottom: 16 }}>
            <Heading2>Combustível e eletrônica</Heading2>
            <Paragraph>
              O motor possui uma alimentação com
              <strong>{` ${Combustivel['Alimentação']} `}</strong>
              movido à<strong>{` ${Combustivel['Combustível']} `}</strong>
              com capacidade de
              <strong>{` ${Combustivel['Tanque de combustível (incluíndo reserva)']} `}</strong>
              mais detalhes abaixo:
            </Paragraph>
          </Section>
          <TableSection>
            <Table>
              <tbody>
                <tr>
                  <th colSpan={2}>
                    <strong>Combustível</strong>
                  </th>
                </tr>
                {Object.entries(Combustivel).map((entry: any) => (
                  <tr key={entry[0]}>
                    <td>{entry[0]}</td>
                    <td>{entry[1].replace('- Mais informações', '')}</td>
                  </tr>
                ))}
                <tr>
                  <th colSpan={2}>
                    <strong>Eletrônica</strong>
                  </th>
                </tr>
                {Object.entries(Eletronica).map((entry: any) => (
                  <tr key={entry[0]}>
                    <td>{entry[0]}</td>
                    <td>{entry[1].replace('- Mais informações', '')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableSection>

          <Section style={{ paddingTop: 16, paddingBottom: 16 }}>
            <Heading2>Transmissão</Heading2>
            <Paragraph>
              A transmissão é composta por uma embreagem do tipo
              <strong>{` ${Transmissao.Embreagem}`}</strong>, câmbio
              <strong>{` ${Transmissao['Câmbio']} `}</strong>e transmissão final
              <strong>{` ${Transmissao['Transmissão final']} `}</strong>
              que possui as seguintes características:
            </Paragraph>
            <ul style={{ listStyle: 'disc', paddingLeft: 32, marginTop: 16 }}>
              <li>
                <Paragraph>
                  Prós:
                  {` ${pros}`}
                </Paragraph>
              </li>
              <li>
                <Paragraph>
                  Contras:
                  {` ${contras}`}
                </Paragraph>
              </li>
            </ul>
          </Section>
          <TableSection>
            <Table>
              <tbody>
                <tr>
                  <th colSpan={2}>
                    <strong>Transmissão</strong>
                  </th>
                </tr>
                {Object.entries(Transmissao).map((entry: any) => (
                  <tr key={entry[0]}>
                    <td>{entry[0]}</td>
                    <td>{entry[1].replace('- Mais informações', '')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableSection>

          {category && (
            <>
              <Section style={{ paddingTop: 16, paddingBottom: 16 }}>
                <Heading2>Categoria</Heading2>
                <Paragraph>
                  O modelo é da categoria <strong>{category}</strong>, que tem
                  como características {caracts}
                </Paragraph>
              </Section>
            </>
          )}

          <TableSection>
            <Heading3 style={{ marginBottom: 16 }}>Mais informações</Heading3>
            <Table>
              <tbody>
                {keys.map((key) => (
                  <React.Fragment key={key}>
                    <tr>
                      <th colSpan={2}>
                        <strong>{key}</strong>
                      </th>
                    </tr>
                    {Object.entries(stats[key]).map((entry: any) => (
                      <tr key={entry[0]}>
                        <td>{entry[0]}</td>
                        <td>{entry[1].replace('- Mais informações', '')}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
            <Spacing top={4}>
              <Paragraph style={{ fontSize: 12 }}>
                * Os valores de seguro apresentados referem-se ao perfil padrão
                adotado pela Luna para comparação de seguros. O valor pode
                variar dependendo do perfil de cada segurado.
              </Paragraph>
            </Spacing>
          </TableSection>

          <FlexSection pastel>
            <Heading2 center>Seguro</Heading2>
            <Spacing top={2} bottom={4}>
              <Paragraph center>
                Com a Luna você consegue ver o valor do seguro em menos de 1
                minuto, clique no botão abaixo e confira o valor do seguro para
                sua moto.
              </Paragraph>
            </Spacing>
            <Link href={ROUTES.startForm()}>
              <a>
                <PrimaryButton pink>Fazer Cotação</PrimaryButton>
              </a>
            </Link>
          </FlexSection>
          {!isAmp && (
            <FlexSection style={{ display: 'grid' }}>
              <Heading2 center>Compare com outra moto</Heading2>
              <Spacing top={2} bottom={4}>
                <Paragraph center>
                  Vejas as diferenças entre cada moto
                </Paragraph>
              </Spacing>
              <CompareStatsForm
                modelA={{ brandName, model, modelName }}
                stats={allStats}
              />
            </FlexSection>
          )}
        </div>
        {!isAmp && (
          <Aside>
            <InnerAside>
              <Spacing bottom={4}>
                <Heading3>Compare com outra moto</Heading3>
              </Spacing>
              <CompareStatsForm
                modelA={{ brandName, model, modelName }}
                stats={allStats}
              />
              <Hr />
              <Heading3 center>
                Seguro para
                {` ${brandName}`}
              </Heading3>
              <Spacing top={2} bottom={4}>
                <Paragraph center>
                  Contrate o seguro para a {` ${modelName} `}
                  em menos de 2 minutos
                </Paragraph>
              </Spacing>
              <Link href={ROUTES.startForm()}>
                <a>
                  <PrimaryButton isBlock>Ver Preços</PrimaryButton>
                </a>
              </Link>
            </InnerAside>
          </Aside>
        )}
      </Wrapper>
      <Hr />
      <Section>
        <Heading2 style={{ marginBottom: 32 }} center>
          Ficha técnica de motos da
          {` ${brandName}`}
        </Heading2>
        <GridLinkList
          links={statsFromBrand.map((s) => ({
            path: ROUTES.modelStats(s.brand, s.model),
            label: `Ficha técnica ${brandName} - ${s.modelName}`,
          }))}
         />
      </Section>
    </PageContainer>
  );
};

export const config = { amp: 'hybrid' };

export const getServerSideProps: GetServerSideProps<Props> = async ({
  params,
}) => {
  const model = params?.model?.toString() || '';
  const brand = params?.brand?.toString() || '';

  const [stats, allStats] = await Promise.all([
    motorcycleStats.getById(`${brand}/${model}`),
    motorcycleStats.getAll(),
  ]);

  if (!stats) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      stats,
      allStats,
    },
  };
};

export default ModelStats;
